'use client';

import { useEffect, useRef, useState } from 'react';

// Crew density data per US state. Emptied — the DB was wiped, so the map
// renders zero coverage everywhere until real crew data is wired in.
const STATE_CREW_DATA: Record<string, { name: string; crews: number; capacity: number; metro?: string }> = {};

// Map FIPS state names to abbreviations
const STATE_NAME_TO_ABBR: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
  'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA',
  'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN',
  'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
  'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
};

function getCrewColor(crews: number): string {
  if (crews === 0) return '#E5E7EB';
  if (crews <= 3) return '#E89A3C';
  if (crews <= 9) return '#1C2E5E';
  return '#1C2E5E';
}

interface TooltipInfo {
  visible: boolean;
  x: number;
  y: number;
  stateName: string;
  crews: number;
  capacity: number;
  metro?: string;
}

interface NationalCapacityHeatMapProps {
  selectedState: string | null;
  onStateClick: (abbr: string | null, stateName: string) => void;
}

const NationalCapacityHeatMap = ({ selectedState, onStateClick }: NationalCapacityHeatMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const layersRef = useRef<Map<string, any>>(new Map());
  const [tooltip, setTooltip] = useState<TooltipInfo>({
    visible: false, x: 0, y: 0, stateName: '', crews: 0, capacity: 0,
  });
  const [mapReady, setMapReady] = useState(false);

  const totalCrews = Object.values(STATE_CREW_DATA).reduce((sum, s) => sum + s.crews, 0);

  // Update layer styles when selectedState changes
  useEffect(() => {
    layersRef.current.forEach((layer, abbr) => {
      const data = STATE_CREW_DATA[abbr];
      const crews = data?.crews ?? 0;
      const isSelected = abbr === selectedState;
      layer.setStyle({
        fillColor: isSelected ? '#F59E0B' : getCrewColor(crews),
        fillOpacity: isSelected ? 0.95 : 0.85,
        color: isSelected ? '#F59E0B' : '#FFFFFF',
        weight: isSelected ? 3 : 1.5,
      });
      if (isSelected) layer.bringToFront();
    });
  }, [selectedState]);

  useEffect(() => {
    if (typeof window === 'undefined' || !mapRef.current || leafletMapRef.current) return;

    import('leaflet').then((L) => {
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      if (!mapRef.current || leafletMapRef.current) return;

      const map = L.map(mapRef.current, {
        center: [38.5, -96],
        zoom: 4,
        zoomControl: true,
        scrollWheelZoom: false,
        dragging: true,
        attributionControl: false,
        minZoom: 3,
        maxZoom: 8,
      });

      leafletMapRef.current = map;

      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(map);

      fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json')
        .then((res) => res.json())
        .then((geojson) => {
          L.geoJSON(geojson, {
            style: (feature) => {
              const stateName = feature?.properties?.name || '';
              const abbr = STATE_NAME_TO_ABBR[stateName];
              const data = abbr ? STATE_CREW_DATA[abbr] : null;
              const crews = data?.crews ?? 0;
              return {
                fillColor: getCrewColor(crews),
                fillOpacity: 0.85,
                color: '#FFFFFF',
                weight: 1.5,
              };
            },
            onEachFeature: (feature, layer) => {
              const stateName = feature?.properties?.name || '';
              const abbr = STATE_NAME_TO_ABBR[stateName];
              const data = abbr ? STATE_CREW_DATA[abbr] : null;

              if (abbr) {
                layersRef.current.set(abbr, layer);
              }

              layer.on({
                mouseover: (e: any) => {
                  const lyr = e.target;
                  const isSelected = abbr === selectedState;
                  if (!isSelected) {
                    lyr.setStyle({ weight: 2.5, fillOpacity: 0.95, color: '#FFFFFF' });
                    lyr.bringToFront();
                  }

                  const containerRect = mapRef.current?.getBoundingClientRect();
                  if (containerRect) {
                    const mouseX = e.originalEvent.clientX - containerRect.left;
                    const mouseY = e.originalEvent.clientY - containerRect.top;
                    setTooltip({
                      visible: true,
                      x: mouseX,
                      y: mouseY,
                      stateName: data?.metro || stateName,
                      crews: data?.crews ?? 0,
                      capacity: data?.capacity ?? 0,
                      metro: data?.metro,
                    });
                  }
                },
                mousemove: (e: any) => {
                  const containerRect = mapRef.current?.getBoundingClientRect();
                  if (containerRect) {
                    const mouseX = e.originalEvent.clientX - containerRect.left;
                    const mouseY = e.originalEvent.clientY - containerRect.top;
                    setTooltip((prev) => ({ ...prev, x: mouseX, y: mouseY }));
                  }
                },
                mouseout: (e: any) => {
                  const lyr = e.target;
                  const isSelected = abbr === selectedState;
                  if (!isSelected) {
                    const crews = data?.crews ?? 0;
                    lyr.setStyle({ weight: 1.5, fillOpacity: 0.85, color: '#FFFFFF', fillColor: getCrewColor(crews) });
                  }
                  setTooltip((prev) => ({ ...prev, visible: false }));
                },
                click: () => {
                  if (abbr) {
                    onStateClick(abbr, stateName);
                  }
                },
              });
            },
          }).addTo(map);

          setMapReady(true);
        })
        .catch((err) => {
          console.error('Failed to load GeoJSON:', err);
          setMapReady(true);
        });
    });

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        layersRef.current.clear();
      }
    };
  }, []);

  // Inject Leaflet CSS
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const existingLink = document.getElementById('leaflet-css');
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div
      className="bg-white rounded-xl shadow-md border border-gray-100 mb-6 overflow-hidden"
      style={{ borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div>
          <h2 className="text-base font-bold text-[#1C2E5E] leading-tight">National Crew Capacity</h2>
          <p className="text-xs text-gray-400 mt-0.5">Active crew density by state · Hover for details · Click to filter crews</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedState && (
            <button
              onClick={() => onStateClick(null, '')}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-all"
            >
              Clear filter
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <span className="text-xs font-semibold text-[#E89A3C] bg-[#EFF9FE] px-3 py-1 rounded-full">
            {totalCrews} Total Crews
          </span>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative w-full" style={{ height: '440px', background: '#F8F9FA' }}>
        {/* Leaflet map div */}
        <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />

        {/* Floating Tooltip */}
        {tooltip.visible && (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              left: tooltip.x + 14,
              top: tooltip.y - 10,
              transform: tooltip.x > 700 ? 'translateX(-110%)' : 'none',
            }}
          >
            <div
              className="rounded-lg px-3 py-2 shadow-xl"
              style={{ background: '#0F172A', minWidth: '180px' }}
            >
              <p className="text-white font-semibold text-xs leading-tight">{tooltip.stateName}</p>
              <p className="text-[#E89A3C] text-xs mt-1">
                {tooltip.crews} Active Crew{tooltip.crews !== 1 ? 's' : ''}
              </p>
              <p className="text-gray-400 text-xs">{tooltip.capacity}% Capacity</p>
              <p className="text-gray-500 text-[10px] mt-1">Click to filter crews</p>
            </div>
          </div>
        )}

        {/* Legend */}
        <div
          className="absolute bottom-4 right-4 z-10 bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3"
          style={{ zIndex: 1000 }}
        >
          <p className="text-[10px] font-bold text-[#1C2E5E] uppercase tracking-wider mb-2">Crew Density</p>
          {[
            { color: '#E5E7EB', label: '0 Crews', sub: 'No Coverage' },
            { color: '#E89A3C', label: '1–3 Crews', sub: 'Low' },
            { color: '#1C2E5E', label: '4–9 Crews', sub: 'Medium' },
            { color: '#1C2E5E', label: '10+ Crews', sub: 'High' },
            { color: '#F59E0B', label: 'Selected', sub: 'Active Filter' },
          ].map(({ color, label, sub }) => (
            <div key={label} className="flex items-center gap-2 mb-1.5 last:mb-0">
              <div
                className="rounded-sm flex-shrink-0"
                style={{ width: 14, height: 14, background: color, border: '1px solid #E5E7EB' }}
              />
              <span className="text-[11px] text-gray-700 font-medium">{label}</span>
              <span className="text-[10px] text-gray-400">· {sub}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NationalCapacityHeatMap;
